/**
 * CallScript — раскрывающийся блок скрипта разговора.
 * Показывает: open, questions, pitch, objections (аккордеон), close.
 */

import { useState } from 'react'
import { ChevronDown, ChevronUp, MessageSquare } from 'lucide-react'
import type { CallScriptData } from '@/types'

interface CallScriptProps {
  script: CallScriptData
  /** По умолчанию развёрнут или свёрнут */
  defaultOpen?: boolean
}

export function CallScript({ script, defaultOpen = false }: CallScriptProps) {
  const [open, setOpen] = useState(defaultOpen)
  const [openObjection, setOpenObjection] = useState<number | null>(null)

  return (
    <div className="rounded-md border border-slate-200 overflow-hidden">
      <button
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
        onClick={() => setOpen((v) => !v)}
      >
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-slate-500" />
          <span className="text-sm font-medium text-slate-700">Скрипт разговора</span>
        </div>
        {open ? (
          <ChevronUp className="h-4 w-4 text-slate-400" />
        ) : (
          <ChevronDown className="h-4 w-4 text-slate-400" />
        )}
      </button>

      {open && (
        <div className="px-4 py-3 space-y-4 bg-white text-sm">
          {/* Открытие */}
          {script.open && (
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Открытие</p>
              <p className="text-slate-700 leading-relaxed italic">«{script.open}»</p>
            </div>
          )}

          {/* Вопросы */}
          {script.questions.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Вопросы</p>
              <ul className="space-y-1">
                {script.questions.map((q, i) => (
                  <li key={i} className="flex items-start gap-2 text-slate-700">
                    <span className="text-slate-400 shrink-0 mt-0.5 text-xs font-mono">{i + 1}.</span>
                    <span className="leading-relaxed">{q}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Питч */}
          {script.pitch && (
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Презентация пользы</p>
              <p className="text-slate-700 leading-relaxed">{script.pitch}</p>
            </div>
          )}

          {/* Возражения */}
          {script.objections.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Возражения</p>
              <div className="space-y-1">
                {script.objections.map((obj, i) => (
                  <div key={i} className="rounded border border-slate-100 overflow-hidden">
                    <button
                      className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-slate-50 transition-colors"
                      onClick={() => setOpenObjection(openObjection === i ? null : i)}
                    >
                      <span className="text-slate-700 font-medium text-xs">— {obj.q}</span>
                      <ChevronDown
                        className={`h-3 w-3 text-slate-400 transition-transform ${openObjection === i ? 'rotate-180' : ''}`}
                      />
                    </button>
                    {openObjection === i && (
                      <div className="px-3 pb-2 text-xs text-slate-600 leading-relaxed bg-slate-50 border-t border-slate-100">
                        {obj.a}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Закрытие */}
          {script.close && (
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Закрытие / следующий шаг</p>
              <p className="text-slate-700 leading-relaxed italic">«{script.close}»</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
